import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useDebounce } from "@/hooks/useDebounce";
import type { FilterState } from "@/types/dashboard";

export const FILTER_COUNTS_TOTAL_KEY = ["filter-counts", "total"] as const;

type FilterCountsSerialized = {
  keywords: string[];
  geo: string[];
  siteTypes: string[];
  status: string[];
  languages: string[];
  from: string | null;
  to: string | null;
};

const serializeFilters = (filters: FilterState): FilterCountsSerialized => ({
  keywords: filters.keywords,
  geo: filters.geo,
  siteTypes: filters.siteTypes,
  status: filters.status,
  languages: filters.languages,
  from: filters.dateRange.from?.toISOString() ?? null,
  to: filters.dateRange.to?.toISOString() ?? null,
});

export const getFilterCountsFilteredKey = (filters: FilterState) =>
  ["filter-counts", "filtered", serializeFilters(filters)] as const;

// Same rows and filters as DashboardService, or the count and the table disagree.
const baseDomainsCountQuery = () =>
  supabase.from("dashboard_domains").select("*", { count: "exact", head: true }).eq("is_active", true);

type DomainsHeadCountQuery = ReturnType<typeof baseDomainsCountQuery>;

const applyFilterState = (
  query: DomainsHeadCountQuery,
  filters: FilterState,
): DomainsHeadCountQuery => {
  const { keywords, geo, siteTypes, status, languages, dateRange } = filters;
  let q = query;

  if (keywords.length > 0) q = q.in("jetsik_brand", keywords);
  if (geo.length > 0) q = q.overlaps("jetsik_countries", geo);
  if (siteTypes.length > 0) q = q.in("site_type", siteTypes);
  if (status.length > 0) q = q.in("status", status);
  if (languages.length > 0) q = q.overlaps("jetsik_languages", languages);
  if (dateRange.from) q = q.gte("created_at", dateRange.from.toISOString());
  if (dateRange.to) q = q.lte("created_at", dateRange.to.toISOString());

  return q;
};

const fetchDomainCount = async (
  query: DomainsHeadCountQuery,
  signal: AbortSignal,
): Promise<number> => {
  const { count, error } = await query.abortSignal(signal);
  if (error) throw error;
  return count ?? 0;
};

const fetchTotalCount = (signal: AbortSignal) =>
  fetchDomainCount(baseDomainsCountQuery(), signal);

const fetchFilteredCount = (filters: FilterState, signal: AbortSignal) =>
  fetchDomainCount(applyFilterState(baseDomainsCountQuery(), filters), signal);

export function useFilterCounts(filters: FilterState) {
  const debouncedFilters = useDebounce(filters, 500);

  const totalQuery = useQuery({
    queryKey: FILTER_COUNTS_TOTAL_KEY,
    queryFn: ({ signal }) => fetchTotalCount(signal),
    staleTime: Infinity,
  });

  const filteredQuery = useQuery({
    queryKey: getFilterCountsFilteredKey(debouncedFilters),
    queryFn: ({ signal }) => fetchFilteredCount(debouncedFilters, signal),
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    totalCount: totalQuery.data ?? null,
    filteredCount: filteredQuery.data ?? null,
    isCounting: totalQuery.isFetching || filteredQuery.isFetching,
  };
}
